import { useRef, useEffect, useCallback } from 'react'
import type { Particle, ThemeMode, ChainConfig } from '@/types'
import { vec2 } from '@/utils/vector'
import { useChainPhysics } from '@/hooks/useChainPhysics'
import { useChainInteraction } from '@/hooks/useChainInteraction'

interface BeadedChainProps {
    onToggle: () => void
    theme: ThemeMode
    config: ChainConfig
}

const CANVAS_CSS_HEIGHT = 400
const ANCHOR_Y_CSS = 10
const FIXED_DT = 1 / 60
const MAX_STEPS = 3

function getChainLength(config: ChainConfig): number {
    return (config.beadCount - 1) * config.segmentLength + config.handleRadius
}

export function BeadedChain({ onToggle, theme, config }: BeadedChainProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1

    // Width = 2x chain length so a fully horizontal chain fits on either side
    const chainLength = getChainLength(config)
    const canvasCssWidth = chainLength * 2 + config.handleRadius * 2

    const anchorPoint = vec2(
        (canvasCssWidth * dpr) / 2,
        ANCHOR_Y_CSS * dpr,
    )

    const scaledConfig: ChainConfig = {
        ...config,
        beadRadius: config.beadRadius * dpr,
        handleRadius: config.handleRadius * dpr,
        segmentLength: config.segmentLength * dpr,
        pullThreshold: config.pullThreshold * dpr,
    }

    const {
        particlesRef,
        step,
        setParticlePosition,
        applyImpulse,
        getRestYPosition,
    } = useChainPhysics({
        config: scaledConfig,
        anchorPoint,
    })

    const { isDraggingRef } = useChainInteraction({
        canvasRef,
        particlesRef,
        config: scaledConfig,
        onToggle,
        setParticlePosition,
        applyImpulse,
        getRestYPosition,
    })

    const drawChain = useCallback(
        (ctx: CanvasRenderingContext2D, particles: Particle[], currentTheme: ThemeMode) => {
            const width = canvasCssWidth * dpr
            const height = CANVAS_CSS_HEIGHT * dpr
            ctx.clearRect(0, 0, width, height)

            // Draw mounting bracket at top
            const mountWidth = 12 * dpr
            const mountHeight = 6 * dpr
            ctx.fillStyle = currentTheme === 'light' ? '#a1a1aa' : '#71717a'
            ctx.beginPath()
            ctx.roundRect(
                anchorPoint.x - mountWidth / 2,
                0,
                mountWidth,
                mountHeight,
                [0, 0, 3 * dpr, 3 * dpr],
            )
            ctx.fill()

            // Colors based on theme
            const beadColorLight = currentTheme === 'light' ? '#a1a1aa' : '#d4d4d8'
            const beadColorDark = currentTheme === 'light' ? '#52525b' : '#a1a1aa'
            const lineColor = currentTheme === 'light' ? '#d4d4d8' : '#71717a'
            const handleColorLight = currentTheme === 'light' ? '#a1a1aa' : '#fbbf24'
            const handleColorDark = currentTheme === 'light' ? '#3f3f46' : '#92400e'

            // Draw connecting lines
            ctx.strokeStyle = lineColor
            ctx.lineWidth = 1 * dpr
            ctx.beginPath()
            for (let i = 0; i < particles.length - 1; i++) {
                const a = particles[i]
                const b = particles[i + 1]
                if (i === 0) {
                    ctx.moveTo(a.position.x, a.position.y)
                }
                ctx.lineTo(b.position.x, b.position.y)
            }
            ctx.stroke()

            // Draw beads
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i]
                const isHandle = i === particles.length - 1

                const colorLight = isHandle ? handleColorLight : beadColorLight
                const colorDark = isHandle ? handleColorDark : beadColorDark

                // Metallic gradient
                const gradient = ctx.createRadialGradient(
                    p.position.x - p.radius * 0.3,
                    p.position.y - p.radius * 0.3,
                    0,
                    p.position.x,
                    p.position.y,
                    p.radius,
                )
                gradient.addColorStop(0, colorLight)
                gradient.addColorStop(1, colorDark)

                ctx.beginPath()
                ctx.arc(p.position.x, p.position.y, p.radius, 0, Math.PI * 2)
                ctx.fillStyle = gradient
                ctx.fill()

                // Subtle highlight on handle
                if (isHandle) {
                    const highlight = ctx.createRadialGradient(
                        p.position.x - p.radius * 0.4,
                        p.position.y - p.radius * 0.4,
                        0,
                        p.position.x - p.radius * 0.2,
                        p.position.y - p.radius * 0.2,
                        p.radius * 0.6,
                    )
                    highlight.addColorStop(0, 'rgba(255, 255, 255, 0.4)')
                    highlight.addColorStop(1, 'rgba(255, 255, 255, 0)')
                    ctx.beginPath()
                    ctx.arc(p.position.x, p.position.y, p.radius, 0, Math.PI * 2)
                    ctx.fillStyle = highlight
                    ctx.fill()
                }
            }
        },
        [dpr, anchorPoint, canvasCssWidth],
    )

    // Track cursor state
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const updateCursor = () => {
            canvas.style.cursor = isDraggingRef.current ? 'grabbing' : 'grab'
            requestAnimationFrame(updateCursor)
        }
        const id = requestAnimationFrame(updateCursor)
        return () => cancelAnimationFrame(id)
    }, [isDraggingRef])

    // Animation loop
    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        let animationId: number
        let lastTime = performance.now()
        let accumulator = 0

        function animate(time: number) {
            const realDt = Math.min((time - lastTime) / 1000, 0.1)
            lastTime = time
            accumulator += realDt

            let steps = 0
            while (accumulator >= FIXED_DT && steps < MAX_STEPS) {
                step(FIXED_DT)
                accumulator -= FIXED_DT
                steps++
            }

            drawChain(ctx!, particlesRef.current, theme)
            animationId = requestAnimationFrame(animate)
        }

        animationId = requestAnimationFrame(animate)
        return () => cancelAnimationFrame(animationId)
    }, [step, drawChain, particlesRef, theme])

    return (
        <canvas
            ref={canvasRef}
            width={canvasCssWidth * dpr}
            height={CANVAS_CSS_HEIGHT * dpr}
            style={{
                width: canvasCssWidth,
                height: CANVAS_CSS_HEIGHT,
                touchAction: 'auto',
                cursor: 'grab',
            }}
        />
    )
}
